import { useEffect, useRef, useState, useSyncExternalStore, type ComponentType, type CSSProperties } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import { useSessione } from '../servizi/sessione.tsx'
import { Iniziali, Pie } from './Pezzi.tsx'
import { Bottone, BottoneIcona } from './base.tsx'
import { InterruttorePalestra, NastroPalestra } from './Palestra.tsx'
import { FasciaImpersonazione } from './FasciaImpersonazione.tsx'
import {
  IconaAmministrazione, IconaApprovazioni, IconaCalendario, IconaCampana, IconaCruscotto,
  IconaDocumento,
  IconaChiudi, IconaEnti, IconaEsci, IconaGruppo, IconaInterventi, IconaMediatori, IconaMenu, IconaMessaggi,
  IconaBusta, IconaMigrazione, IconaRegistrazione, IconaPersone, IconaProgetti, IconaRegistro, IconaReport,
  IconaServiziErogati,
} from './Icone.tsx'
import { IconaAggiorna, IconaInfo, IconaLucchetto } from './Icone.tsx'
import { AvvisoNovita } from './AvvisoNovita.tsx'
import { AvvisoSecondoFattore } from './AvvisoSecondoFattore.tsx'
import { AvvisoCaselleDaAssociare } from './AvvisoCaselleDaAssociare.tsx'
import { AvvisoEmailRecupero } from './AvvisoEmailRecupero.tsx'
import { AvvisoAppTelefono } from './AvvisoAppTelefono.tsx'
import { AvvisoNotificheComputer } from './AvvisoNotificheComputer.tsx'
import { AvvisiSchermo } from './AvvisiSchermo.tsx'

/** `permesso`: basta uno di quelli indicati. `senza`: la voce non compare a chi ne ha uno (ha gia' la sua). */
type Voce = {
  a: string; nome: string; permesso: string | string[] | null; senza?: string[]; icona: ComponentType<{ className?: string }>
}

/** Voci di menu: ognuna compare solo se l'utente ha il permesso che la giustifica. */
const VOCI: { gruppo: string; voci: Voce[] }[] = [
  { gruppo: 'Lavoro', voci: [
    { a: '/', nome: 'Cruscotto', permesso: null, icona: IconaCruscotto },
    { a: '/persone', nome: 'Beneficiari', permesso: 'persona.cerca', icona: IconaPersone },
    // 460: le schede a cui mancano dati, raggruppate per dato, e le firme di terzi da completare.
    { a: '/da-completare', nome: 'Da completare', permesso: 'persona.cerca', icona: IconaApprovazioni },
    { a: '/interventi', nome: 'Interventi', permesso: 'intervento.leggi', icona: IconaInterventi },
    { a: '/follow-up', nome: 'Ricontatti', permesso: 'follow_up.gestisci', icona: IconaCalendario },
    { a: '/attivita', nome: 'Attività collettive', permesso: 'attivita_collettiva.leggi', icona: IconaGruppo },
    // Il mediatore: le mediazioni che gli hanno assegnato (indicazioni CIDIS del 17/10/2026).
    { a: '/mediazioni', nome: 'Le mie mediazioni', permesso: 'mediazione.propria', icona: IconaMediatori },
    // Calendari (557): delle mediazioni per chi ha `agenda.leggi`, il proprio per il mediatore.
    // Agenda della sede (559): appuntamenti, mediazioni, turni, stanze, riunioni.
    { a: '/agenda', nome: 'Agenda', permesso: 'agenda.leggi', icona: IconaCalendario },
    { a: '/mediazioni/calendario', nome: 'Calendario delle mediazioni', permesso: 'agenda.leggi', icona: IconaCalendario },
    { a: '/mediazioni/mio-calendario', nome: 'Il mio calendario', permesso: 'mediazione.propria', icona: IconaCalendario },
    // 563: i propri impegni per chiunque abbia un'utenza. Chi ha l'agenda delle sedi ci trova «La mia agenda»,
    // il mediatore li trova in «Il mio calendario».
    { a: '/agenda?sede=mia', nome: 'La mia agenda', permesso: 'agenda.propria', senza: ['agenda.leggi', 'mediazione.propria'], icona: IconaCalendario },
  ] },
  // Il registro dei servizi erogati prende il posto delle richieste degli enti (D-01).
  { gruppo: 'Rete', voci: [
    { a: '/enti', nome: 'Enti', permesso: 'ente.leggi', icona: IconaEnti },
    { a: '/registro', nome: 'Registro dei servizi erogati', permesso: 'servizio_erogato.leggi', icona: IconaServiziErogati },
    // Chi gestisce l'albo (anche l'amministrazione, che non ha `mediatore.leggi`) lo vede (19/10/2026).
    { a: '/mediatori', nome: 'Mediatori', permesso: ['mediatore.leggi', 'mediatore.gestisci'], icona: IconaMediatori },
    { a: '/progetti', nome: 'Progetti', permesso: 'progetto.leggi', icona: IconaProgetti },
  ] },
  { gruppo: 'Comunicazione', voci: [
    // Dal 28/10/2026 (479) la pagina presenta il messenger su Matrix e lo apre: il percorso resta.
    { a: '/messaggi', nome: 'Messenger', permesso: 'chat.usa', icona: IconaMessaggi },
    { a: '/notifiche', nome: 'Notifiche', permesso: null, icona: IconaCampana },
    // Chi usa la posta del gestionale, o configura la casella dell'ente. Dal 23/10/2026 (471) il
    // mediatore non ha piu' posta.usa: la voce non gli porta a una pagina vuota.
    { a: '/posta', nome: 'Posta', permesso: ['posta.usa', 'impostazione.modifica'], icona: IconaBusta },
  ] },
  { gruppo: 'Controllo', voci: [
    { a: '/report', nome: 'Report', permesso: 'report.aggregato', icona: IconaReport },
    { a: '/audit', nome: 'Registro eventi', permesso: 'audit.leggi', icona: IconaRegistro },
    { a: '/approvazioni', nome: 'Approvazioni', permesso: 'approvazione.richiedi', icona: IconaApprovazioni },
    { a: '/registrazioni', nome: 'Registrazioni schermo', permesso: 'registrazione.visiona', icona: IconaRegistrazione },
    { a: '/migrazione', nome: 'Migrazione', permesso: 'migrazione.leggi', icona: IconaMigrazione },
    { a: '/amministrazione', nome: 'Amministrazione', permesso: 'utente.leggi', icona: IconaAmministrazione },
    // Onda 2: le sezioni nuove dell amministrazione sono pagine con percorso proprio, non
    // linguette dentro Amministrazione.tsx.
    // Dal 556 anche chi scrive orari e chiusure (Project Manager, coordinatori) senza gestire le sedi.
    { a: '/sedi', nome: 'Sedi', permesso: ['sede.gestisci', 'sede.agenda'], icona: IconaEnti },
    { a: '/amministrazione/email', nome: 'Registro delle email', permesso: 'email.registro', icona: IconaBusta },
    { a: '/decisioni', nome: 'Decisioni', permesso: 'decisione.leggi', icona: IconaDocumento },
    // 490: la sede di formazione, il suo stato e il ripristino, per chi conduce la formazione.
    { a: '/formazione', nome: 'Formazione', permesso: 'formazione.gestisci', icona: IconaGruppo },
    // L'amministratore gestisce il catalogo dei fondi (D-24) senza avere progetto.leggi: senza questa
    // voce la pagina non si raggiunge dal menu.
    { a: '/fondi', nome: 'Catalogo dei fondi', permesso: 'catalogo.gestisci', icona: IconaProgetti },
    // D-18: l'amministratore aggiunge voci ai servizi bancari e ai motivi di «NESSUNO di questi servizi».
    { a: '/cataloghi-bancari', nome: 'Cataloghi dei servizi bancari', permesso: 'catalogo.gestisci', icona: IconaAmministrazione },
  ] },
  // La propria utenza: il profilo con i telefoni della firma (508) e il secondo fattore (493), per tutti.
  { gruppo: 'Il tuo accesso', voci: [
    { a: '/profilo', nome: 'Il mio profilo', permesso: null, icona: IconaPersone },
    { a: '/secondo-fattore', nome: 'Secondo fattore', permesso: null, icona: IconaLucchetto },
  ] },
  // Guida illustrata e Novità (31/10/2026): per tutti; i capitoli si filtrano secondo gli incarichi.
  { gruppo: 'Aiuto', voci: [
    { a: '/guida', nome: 'Guida', permesso: null, icona: IconaInfo },
    { a: '/novita', nome: 'Novità', permesso: null, icona: IconaAggiorna },
  ] },
]

/** Osserva la larghezza: sotto gli 860 px il menu diventa a scomparsa. */
function useSchermoAmpio() {
  const query = typeof window === 'undefined' ? null : window.matchMedia('(min-width: 861px)')
  return useSyncExternalStore(
    (avvisa) => { query?.addEventListener('change', avvisa); return () => query?.removeEventListener('change', avvisa) },
    () => query?.matches ?? true,
    () => true,
  )
}

export function Impalcatura() {
  const larghezzaAmpia = useSchermoAmpio()
  const {
    io, puo, esci, notificheDaLeggere,
    avvisoRegistrazione, modalitaRegistrazione, puoRegistrarePerAssistenza,
    registrazionePerAssistenza,
  } = useSessione()
  const [menuAperto, setMenuAperto] = useState(false)
  // Bordo inferiore di testata e nastro della registrazione, letto all'apertura: il menu a
  // scomparsa comincia li' sotto. Il nastro ha un'altezza che dipende dal testo, e deve restare
  // visibile anche a menu aperto (art. 4 l. 300/1970, stile.css).
  const [cimaMenu, setCimaMenu] = useState<number | null>(null)
  const testata = useRef<HTMLElement>(null)
  const nastro = useRef<HTMLDivElement>(null)
  const apriMenu = useRef<HTMLSpanElement>(null)
  const menu = useRef<HTMLElement>(null)
  const posizione = useLocation()
  const menuVisibile = menuAperto && !larghezzaAmpia

  useEffect(() => { setMenuAperto(false) }, [posizione.pathname])
  // Ruotando il tablet il menu laterale torna fisso: tornando stretti non deve riaprirsi da solo.
  useEffect(() => { if (larghezzaAmpia) setMenuAperto(false) }, [larghezzaAmpia])

  // Menu a scomparsa aperto (sotto gli 860 px): la pagina sotto il velo non scorre, il fuoco va
  // sulla prima voce, Esc chiude e riporta il fuoco sul comando che l'aveva aperto.
  useEffect(() => {
    if (!menuVisibile) return
    const radice = document.documentElement
    radice.classList.add('menu-aperto')
    menu.current?.querySelector<HTMLElement>('a[href]')?.focus()
    const tasto = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setMenuAperto(false)
      apriMenu.current?.querySelector('button')?.focus()
    }
    document.addEventListener('keydown', tasto)
    return () => {
      document.removeEventListener('keydown', tasto)
      radice.classList.remove('menu-aperto')
    }
  }, [menuVisibile])

  function alternaMenu() {
    if (!menuAperto) setCimaMenu((nastro.current ?? testata.current)?.getBoundingClientRect().bottom ?? null)
    setMenuAperto((v) => !v)
  }
  const posizioneMenu = cimaMenu === null ? undefined : { '--cima-menu': `${cimaMenu}px` } as CSSProperties

  return (
    <div className="impalcatura">
      <header className="testata" ref={testata}>
        {!larghezzaAmpia && (
          <span className="apri-menu" ref={apriMenu}>
            <BottoneIcona icona={menuAperto ? <IconaChiudi /> : <IconaMenu />}
                          etichetta={menuAperto ? 'Chiudi la navigazione' : 'Apri la navigazione'}
                          aria-expanded={menuAperto} aria-controls="navigazione"
                          onClick={alternaMenu} />
          </span>
        )}
        <Link to="/" className="marchio">
          <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
          <span className="denominazione">
            Gestionale
            <span className="qualifica"> · Impresa Sociale ETS</span>
          </span>
        </Link>
        {io && (
          <div className="riga">
            {larghezzaAmpia && (
              <div className="utente-testata">
                <Iniziali nome={io.nome} cognome={io.cognome} />
                <div>
                  <div className="nome">{io.nome} {io.cognome}</div>
                  <div className="ruolo">
                    {io.incarichi.map((i) => i.ruolo).join(' · ') || 'Nessun incarico'}
                  </div>
                </div>
              </div>
            )}
            {puoRegistrarePerAssistenza && !modalitaRegistrazione && (
              <BottoneIcona icona={<IconaRegistrazione />}
                            etichetta="Avvia la registrazione dello schermo per assistenza"
                            onClick={() => void registrazionePerAssistenza(true)} />
            )}
            <InterruttorePalestra />
            <BottoneIcona icona={<IconaEsci />} etichetta="Esci"
                          onClick={() => void esci()} />
          </div>
        )}
      </header>

      {avvisoRegistrazione && (
        <div className="nastro-registrazione" role="status" ref={nastro}>
          <span className="pallino-registrazione" aria-hidden="true" />
          <span>{avvisoRegistrazione}</span>
          {modalitaRegistrazione === 'assistenza' && (
            <span className="spinta">
              <Bottone variante="discreto" dimensione="piccolo"
                       onClick={() => void registrazionePerAssistenza(false)}>
                Interrompi
              </Bottone>
            </span>
          )}
        </div>
      )}

      <FasciaImpersonazione />
      <NastroPalestra />

      {/* Velo del menu a scomparsa: toccato fuori dal menu, lo chiude. Da tastiera si chiude con Esc. */}
      {menuVisibile && (
        <div className="velo-menu" aria-hidden="true" style={posizioneMenu}
             onClick={() => setMenuAperto(false)} />
      )}

      <div className="corpo">
        <nav className="navigazione" id="navigazione" hidden={!menuAperto && !larghezzaAmpia}
             aria-label="Sezioni" ref={menu} style={larghezzaAmpia ? undefined : posizioneMenu}>
          {/* Sul telefono la testata non ha posto per nome e incarichi: stanno in cima al menu. */}
          {!larghezzaAmpia && io && (
            <div className="navigazione-utente">
              <Iniziali nome={io.nome} cognome={io.cognome} />
              <div>
                <div className="nome">{io.nome} {io.cognome}</div>
                <div className="ruolo">
                  {io.incarichi.map((i) => i.ruolo).join(' · ') || 'Nessun incarico'}
                </div>
              </div>
            </div>
          )}
          {VOCI.map((g) => {
            const visibili = g.voci.filter((v) => (!v.permesso
              || (Array.isArray(v.permesso) ? v.permesso.some((p) => puo(p)) : puo(v.permesso)))
              && !v.senza?.some((p) => puo(p)))
            if (!visibili.length) return null
            return (
              <div key={g.gruppo}>
                <div className="gruppo">{g.gruppo}</div>
                {visibili.map(({ a, nome, icona: Icona }) => (
                  <NavLink key={a} to={a} end={a === '/'}>
                    <Icona />
                    {nome}
                    {a === '/notifiche' && notificheDaLeggere > 0 && (
                      <span className="etichetta accento spinta">{notificheDaLeggere}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>

        <main className="contenuto">
          <AvvisoSecondoFattore />
          <AvvisoEmailRecupero />
          <AvvisoCaselleDaAssociare />
          <AvvisoNovita />
          <AvvisoAppTelefono />
          <AvvisoNotificheComputer />
          <Outlet />
        </main>
      </div>

      {/* Avvisi sullo schermo (2/11/2026): email e notifiche nuove, in basso a destra. */}
      <AvvisiSchermo />

      <Pie />
    </div>
  )
}
